import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';

export default function BlogPage() {
  const blogSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Good Pitch Roofing Blog',
    description:
      'Expert advice on Oklahoma roof maintenance, insurance claims, storm damage repair, and roofing materials from Good Pitch Roofing.',
    url: `${siteUrl}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'Good Pitch Roofing',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/efcca1ae64e2adea8f6cbcf3fc677bc7/dcb07f32778ecb4afada9f63cf477c36.png',
      },
    },
    blogPost: blogPosts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      author: {
        '@type': 'Person',
        name: post.author,
      },
      datePublished: post.date,
      url: `${siteUrl}/blog/${post.slug}`,
    })),
  }), []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Roofing Blog | Oklahoma Roof Tips, Insurance Claims & Storm Damage | Good Pitch Roofing"
        description="Read expert articles on Oklahoma roof maintenance, insurance claims, storm damage preparation, and choosing the right roofing materials. From Good Pitch Roofing, License #80007301."
        canonical="/blog"
        keywords="roofing blog, Oklahoma roof tips, insurance claim help, storm damage roof, roof maintenance guides"
        ogTitle="Roofing Resources & Insights | Good Pitch Roofing Blog"
        ogDescription="Expert roofing advice from Oklahoma's trusted insurance roofing specialists. Learn about storm damage, insurance claims, and roof maintenance."
        ogType="website"
        schema={blogSchema}
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                <i className="ri-home-smile-line text-2xl text-white"></i>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">Good Pitch Roofing</div>
                <div className="text-xs text-gray-600">License #80007301</div>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#services" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Services</Link>
              <Link to="/#process" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Process</Link>
              <Link to="/#testimonials" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Testimonials</Link>
              <Link to="/blog" className="text-sm font-medium text-orange-500 transition-colors whitespace-nowrap">Blog</Link>
              <Link to="/#contact" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">Contact</Link>
              <a href="tel:405-397-2783" className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>405-397-2783
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative pt-24 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7d35bf48e8314b2deee2f7a74286d139.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Roofing Resources &amp; Insights</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Expert advice on roof maintenance, insurance claims, storm damage, and everything you need to know about protecting your Oklahoma home.</p>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link 
              key={post.slug} 
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="relative w-full h-56 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <i className="ri-calendar-line"></i>
                  <span>{post.date}</span>
                  <span className="mx-2">•</span>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                <div className="flex items-center text-orange-500 font-semibold text-sm group-hover:gap-3 gap-2 transition-all whitespace-nowrap">
                  Read Full Article
                  <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}